import { format } from "date-fns";

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import type { UsersOrderData } from "@/types";
import { Badge } from "@/components/ui/badge";
import getStatusBadgeVariant from "@/utils/getStatusBadgeVariant";
import getStatusLabel from "@/utils/getStatusLabel";

interface OrderHistoryTabProps {
	ordersData: UsersOrderData[];
	setCurrentOrder: (order: UsersOrderData) => void;
	isLoading: boolean;
	searchQuery: string;
}

const OrderHistoryTab = ({
	ordersData,
	setCurrentOrder,
	searchQuery,
}: OrderHistoryTabProps) => {
	const completedOrders = ordersData.filter(
		(order) => order.delivered || order.cancelled,
	);

	const filteredOrders = completedOrders.filter((order) => {
		if (!searchQuery) return true;
		const searchLower = searchQuery.toLowerCase();
		return (
			order.user.fullName?.toLowerCase().includes(searchLower) ||
			format(new Date(order.collectionDate), "PPP")
				.toLowerCase()
				.includes(searchLower) ||
			order.totalPrice.toString().includes(searchLower)
		);
	});

	const ordersByStatus = {
		completed: filteredOrders.filter((order) => order.delivered),
		cancelled: filteredOrders.filter((order) => order.cancelled),
	};

	const handleOrderClick = (order: UsersOrderData) => {
		setCurrentOrder(order);
	};

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader>
					<CardTitle>Order History</CardTitle>
					<CardDescription>
						{searchQuery
							? `Searching for "${searchQuery}" in completed and cancelled orders`
							: "Completed and cancelled orders"}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6 md:grid-cols-2">
						<div className="space-y-4">
							<h3 className="text-lg font-medium">
								Completed Orders ({ordersByStatus.completed.length})
							</h3>
							{ordersByStatus.completed.map((order) => (
								<button
									key={order.id}
									type="button"
									onClick={() => handleOrderClick(order)}
									className="flex w-full items-center justify-between rounded-lg border p-4 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
								>
									<div>
										<p className="font-medium">{order.user.fullName}</p>
										<p className="text-sm text-muted-foreground">
											{format(new Date(order.collectionDate), "PPP")}
										</p>
									</div>
									<div className="text-right">
										<Badge
											className={`text-xs text-white ${getStatusBadgeVariant(
												order,
											)}`}
										>
											{getStatusLabel(order)}
										</Badge>
										<p className="mt-1 font-medium">
											£{order.totalPrice.toFixed(2)}
										</p>
									</div>
								</button>
							))}
						</div>

						<div className="space-y-4">
							<h3 className="text-lg font-medium">Cancelled Orders</h3>
							{ordersByStatus.cancelled.map((order) => (
								<button
									key={order.id}
									type="button"
									onClick={() => handleOrderClick(order)}
									className="flex w-full items-center justify-between rounded-lg border p-4 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
								>
									<div>
										<p className="font-medium">{order.user.fullName}</p>
										<p className="text-sm text-muted-foreground">
											{format(new Date(order.collectionDate), "PPP")}
										</p>
									</div>
									<div className="text-right">
										<Badge
											className={`text-xs text-white ${getStatusBadgeVariant(
												order,
											)}`}
										>
											{getStatusLabel(order)}
										</Badge>
										<p className="mt-1 font-medium">
											£{order.totalPrice.toFixed(2)}
										</p>
									</div>
								</button>
							))}
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
};

export default OrderHistoryTab;
